import { computed, onBeforeUnmount, ref, watch, type Ref } from 'vue';
import type { FileUploadState } from '@magma-app/magma-lapilli';
import { useI18n } from 'vue-i18n';
import { instances } from '@/services/http';

type UploadTarget = 'ugc' | 'event' | 'challenge';

type UploadedFile = {
  s3Key: string;
  originalFilename: string;
  filename: string;
  mimeType: string;
  size: number;
};

type UploadErrorCode =
  | 'unsupported_format'
  | 'file_too_large'
  | 'file_empty'
  | 'total_size_exceeded'
  | 'upload_failed'
  | 'upload_stalled'
  | 'finalization_failed';

interface FileUploadEntry {
  file: File;
  originalFilename: string;
  filename: string;
  status: 'pending' | 'uploading' | 'success' | 'error';
  progress: number;
  error: string | null;
  errorCode: UploadErrorCode | null;
  s3Key: string | null;
  xhr: XMLHttpRequest | null;
  retryCount: number;
  uploadId: string | null;
  parts: {
    partNumber: number;
    presignedUrl: string;
    etag: string | null;
    status: 'pending' | 'uploading' | 'success' | 'error';
  }[] | null;
  stallTimer: ReturnType<typeof setTimeout> | null;
}

const ACCEPTED_MEDIA_MIMES = [
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/gif',
  'image/webp',
  'image/avif',
  'image/heic',
  'video/mp4',
  'video/quicktime',
  'video/webm',
  'video/x-matroska',
  'video/x-m4v',
];

const ACCEPTED_ACTION_MIMES = [
  ...ACCEPTED_MEDIA_MIMES,
  'application/pdf',
];

const EXTENSION_MIME_FALLBACKS: Record<string, string> = {
  avif: 'image/avif',
  gif: 'image/gif',
  heic: 'image/heic',
  jpeg: 'image/jpeg',
  jpg: 'image/jpeg',
  m4v: 'video/x-m4v',
  mkv: 'video/x-matroska',
  mov: 'video/quicktime',
  mp4: 'video/mp4',
  png: 'image/png',
  pdf: 'application/pdf',
  webm: 'video/webm',
  webp: 'image/webp',
};

const MULTIPART_THRESHOLD = 80 * 1024 * 1024;
const PART_SIZE = 10 * 1024 * 1024;
const MAX_CONCURRENT = 3;
const MAX_RETRY = 3;
const STALL_TIMEOUT = 30_000;
const MAX_FILE_SIZE = 300 * 1024 * 1024;
const MAX_TOTAL_SIZE = 2 * 1024 * 1024 * 1024;

const ERROR_TRANSLATION_KEYS: Record<UploadErrorCode, string> = {
  file_empty: 'errors.fileEmpty',
  unsupported_format: 'errors.unsupportedFormat',
  file_too_large: 'errors.fileTooLarge',
  total_size_exceeded: 'errors.totalSizeExceeded',
  upload_failed: 'errors.uploadFailed',
  upload_stalled: 'errors.uploadStalled',
  finalization_failed: 'errors.finalizationFailed',
};

const NON_RETRYABLE_ERROR_CODES = new Set<UploadErrorCode>([
  'file_empty',
  'unsupported_format',
  'file_too_large',
  'total_size_exceeded',
]);

const getFileMimeType = (file: File) => {
  const mimeType = file.type?.trim().toLowerCase();
  if (mimeType) {
    return mimeType;
  }

  const extension = file.name.split('.').pop()?.toLowerCase();
  return extension ? EXTENSION_MIME_FALLBACKS[extension] || '' : '';
};

const isRetryableUploadError = (code: UploadErrorCode | null) =>
  !code || !NON_RETRYABLE_ERROR_CODES.has(code);

export function useUgcFileUpload(
  files: Ref<File[]>,
  userUgcId: Ref<number>
): ReturnType<typeof _useFileUpload>;
export function useUgcFileUpload(
  userUgcId: Ref<number>
): ReturnType<typeof _useFileUpload>;
export function useUgcFileUpload(
  filesOrUserUgcId: Ref<File[]> | Ref<number>,
  maybeUserUgcId?: Ref<number>
) {
  const files = maybeUserUgcId
    ? filesOrUserUgcId as Ref<File[]>
    : ref<File[]>([]);
  const userUgcId = maybeUserUgcId || filesOrUserUgcId as Ref<number>;

  return _useFileUpload(files, userUgcId, 'ugc');
}

export function useActionFileUpload(
  files: Ref<File[]>,
  actionType: 'event' | 'challenge',
  userActionId: Ref<number>
) {
  return _useFileUpload(files, userActionId, actionType);
}

const _useFileUpload = (
  files: Ref<File[]>,
  userActionId: Ref<number>,
  uploadTarget: UploadTarget
) => {
  const { t } = useI18n({ useScope: 'global' });
  const entries = ref<FileUploadEntry[]>([]);
  const activeCount = ref(0);
  const uploadError = ref<string | null>(null);

  const uploadStates = computed<FileUploadState[]>(() =>
    entries.value.map((entry) => ({
      status: entry.status,
      progress: entry.progress,
      error: entry.error,
      retryCount: entry.retryCount,
      retryable: isRetryableUploadError(entry.errorCode),
    }))
  );

  const completedFiles = computed<UploadedFile[]>(() =>
    entries.value
      .filter((entry) => entry.status === 'success' && !!entry.s3Key)
      .map((entry) => ({
        s3Key: entry.s3Key as string,
        originalFilename: entry.originalFilename,
        filename: entry.filename,
        mimeType: getFileMimeType(entry.file),
        size: entry.file.size,
      }))
  );

  const isUploading = computed(() =>
    entries.value.some(
      (entry) => entry.status === 'uploading' || entry.status === 'pending'
    )
  );

  const hasErrors = computed(() =>
    entries.value.some((entry) => entry.status === 'error')
  );

  const allUploaded = computed(() =>
    entries.value.length > 0
    && entries.value.every((entry) => entry.status === 'success')
  );

  const uploadedFiles = completedFiles;

  const getUploadErrorMessage = (code: UploadErrorCode) => {
    const translationKey = ERROR_TRANSLATION_KEYS[code];
    const translatedMessage = t(translationKey);
    if (translatedMessage !== translationKey) {
      return translatedMessage;
    }

    const legacyTranslationKey = `ugcs.ongoing.add.uploadErrors.${code}`;
    const legacyTranslatedMessage = t(legacyTranslationKey);

    return legacyTranslatedMessage === legacyTranslationKey
      ? t('generic.error')
      : legacyTranslatedMessage;
  };

  const getAcceptedMimes = () => uploadTarget === 'ugc'
    ? ACCEPTED_MEDIA_MIMES
    : ACCEPTED_ACTION_MIMES;

  const buildUploadPayload = (payload: Record<string, unknown>) => {
    if (uploadTarget === 'ugc') {
      return {
        ...(userActionId.value ? { userUgcId: userActionId.value } : {}),
        ...payload,
      };
    }

    return {
      actionType: uploadTarget,
      ...(userActionId.value ? { userActionId: userActionId.value } : {}),
      ...payload,
    };
  };

  const postUploadEndpoint = async <TResponse>(
    ugcEndpoint: string,
    actionEndpoint: string,
    payload: Record<string, unknown>
  ) => {
    const endpoint = uploadTarget === 'ugc' ? ugcEndpoint : actionEndpoint;
    const response = await instances.userDashboard.post(
      endpoint,
      buildUploadPayload(payload)
    );

    return response.data as TResponse;
  };

  const getPresignedUrl = async (payload: {
    originalFilename: string;
    mimeType: string;
    fileSize: number;
  }) => postUploadEndpoint<{
    presignedUrl: string;
    s3Key: string;
    expiresIn: number;
  }>(
    'ugcs/upload/presigned-url',
    'actions/upload/presigned-url',
    payload
  );

  const initiateMultipart = async (payload: {
    originalFilename: string;
    mimeType: string;
    fileSize: number;
  }) => postUploadEndpoint<{
    uploadId: string;
    s3Key: string;
    parts: { partNumber: number; presignedUrl: string }[];
  }>(
    'ugcs/upload/multipart/initiate',
    'actions/upload/multipart/initiate',
    payload
  );

  const completeMultipartMutation = async (payload: {
    s3Key: string;
    uploadId: string;
    parts: { partNumber: number; etag: string }[];
  }) => postUploadEndpoint<{ s3Key: string }>(
    'ugcs/upload/multipart/complete',
    'actions/upload/multipart/complete',
    payload
  );

  const logUploadAttempt = async (payload: {
    filename: string;
    mimeType: string;
    fileSize: number;
    reason: string;
  }) => postUploadEndpoint<{ message: string }>(
    'ugcs/upload-attempts',
    'actions/upload-attempts',
    payload
  );

  const logRejectedUpload = async ({ file, mimeType, reason }: {
    file: File;
    mimeType: string;
    reason: string;
  }) => {
    try {
      await logUploadAttempt({
        filename: file.name,
        mimeType,
        fileSize: file.size,
        reason,
      });
    } catch {
      // Upload logging must not block the ambassador from seeing the real error.
    }
  };

  const releaseQueueSlot = () => {
    activeCount.value = Math.max(0, activeCount.value - 1);
  };

  const clearStallTimer = (entry: FileUploadEntry) => {
    if (entry.stallTimer) {
      clearTimeout(entry.stallTimer);
      entry.stallTimer = null;
    }
  };

  const markEntryAsError = (
    entry: FileUploadEntry,
    code: UploadErrorCode
  ) => {
    entry.status = 'error';
    entry.error = getUploadErrorMessage(code);
    entry.errorCode = code;
    uploadError.value = code;
  };

  const getValidationError = (
    file: File,
    existingEntries: FileUploadEntry[] = entries.value
  ): { code: UploadErrorCode; mimeType: string } | null => {
    const mimeType = getFileMimeType(file);

    if (file.size === 0) {
      return { code: 'file_empty', mimeType };
    }

    if (!getAcceptedMimes().includes(mimeType)) {
      return { code: 'unsupported_format', mimeType };
    }

    if (file.size > MAX_FILE_SIZE) {
      return { code: 'file_too_large', mimeType };
    }

    const currentTotalSize = existingEntries.reduce(
      (sum, currentEntry) => {
        if (
          currentEntry.status === 'error'
          && !isRetryableUploadError(currentEntry.errorCode)
        ) {
          return sum;
        }

        return sum + currentEntry.file.size;
      },
      0
    );
    if (currentTotalSize + file.size > MAX_TOTAL_SIZE) {
      return { code: 'total_size_exceeded', mimeType };
    }

    return null;
  };

  const rejectEntryForValidationError = (
    entry: FileUploadEntry,
    validationError: { code: UploadErrorCode; mimeType: string }
  ) => {
    markEntryAsError(entry, validationError.code);
    void logRejectedUpload({
      file: entry.file,
      mimeType: validationError.mimeType,
      reason: validationError.code,
    });
  };

  const handleStall = (entry: FileUploadEntry) => {
    if (!entries.value.includes(entry)) {
      return;
    }

    if (entry.xhr) {
      entry.xhr.abort();
    }

    clearStallTimer(entry);
    entry.xhr = null;
    markEntryAsError(entry, 'upload_stalled');
    releaseQueueSlot();
    processQueue();
  };

  const resetStallTimer = (entry: FileUploadEntry) => {
    clearStallTimer(entry);
    entry.stallTimer = setTimeout(() => handleStall(entry), STALL_TIMEOUT);
  };

  const createEntry = (
    file: File,
    existingEntries: FileUploadEntry[] = entries.value
  ): FileUploadEntry => {
    const entry: FileUploadEntry = {
      file,
      originalFilename: file.name,
      filename: file.name,
      status: 'pending',
      progress: 0,
      error: null,
      errorCode: null,
      s3Key: null,
      xhr: null,
      retryCount: 0,
      uploadId: null,
      parts: null,
      stallTimer: null,
    };

    const validationError = getValidationError(file, existingEntries);
    if (validationError) {
      rejectEntryForValidationError(entry, validationError);
    }

    return entry;
  };

  const startSingleUpload = async (entry: FileUploadEntry) => {
    try {
      const mimeType = getFileMimeType(entry.file);
      const { presignedUrl, s3Key } = await getPresignedUrl({
        originalFilename: entry.originalFilename,
        mimeType,
        fileSize: entry.file.size,
      });

      if (!entries.value.includes(entry)) {
        return;
      }

      entry.s3Key = s3Key;

      const xhr = new XMLHttpRequest();
      entry.xhr = xhr;
      resetStallTimer(entry);

      xhr.upload.onprogress = (event) => {
        if (!entries.value.includes(entry)) {
          return;
        }

        resetStallTimer(entry);
        if (event.lengthComputable) {
          entry.progress = Math.round((event.loaded / event.total) * 100);
        }
      };

      xhr.onload = () => {
        if (!entries.value.includes(entry)) {
          clearStallTimer(entry);
          entry.xhr = null;
          return;
        }

        clearStallTimer(entry);
        if (xhr.status >= 200 && xhr.status < 300) {
          entry.status = 'success';
          entry.progress = 100;
          entry.error = null;
          entry.errorCode = null;
        } else {
          markEntryAsError(entry, 'upload_failed');
        }
        entry.xhr = null;
        releaseQueueSlot();
        processQueue();
      };

      xhr.onerror = () => {
        if (!entries.value.includes(entry)) {
          clearStallTimer(entry);
          entry.xhr = null;
          return;
        }

        clearStallTimer(entry);
        entry.xhr = null;
        markEntryAsError(entry, 'upload_failed');
        releaseQueueSlot();
        processQueue();
      };

      xhr.open('PUT', presignedUrl);
      xhr.setRequestHeader('Content-Type', mimeType);
      xhr.send(entry.file);
    } catch {
      if (entries.value.includes(entry)) {
        markEntryAsError(entry, 'upload_failed');
        releaseQueueSlot();
        processQueue();
      }
    }
  };

  const startMultipartUpload = async (entry: FileUploadEntry) => {
    try {
      if (entry.uploadId && entry.s3Key && entry.parts) {
        await uploadParts(entry);
        return;
      }

      const mimeType = getFileMimeType(entry.file);
      const result = await initiateMultipart({
        originalFilename: entry.originalFilename,
        mimeType,
        fileSize: entry.file.size,
      });

      if (!entries.value.includes(entry)) {
        return;
      }

      entry.s3Key = result.s3Key;
      entry.uploadId = result.uploadId;
      entry.parts = result.parts.map((part) => ({
        partNumber: part.partNumber,
        presignedUrl: part.presignedUrl,
        etag: null,
        status: 'pending' as const,
      }));

      await uploadParts(entry);
    } catch {
      if (entries.value.includes(entry)) {
        markEntryAsError(entry, 'upload_failed');
        releaseQueueSlot();
        processQueue();
      }
    }
  };

  const uploadParts = async (entry: FileUploadEntry) => {
    if (!entry.parts) {
      return;
    }

    const totalSize = entry.file.size;

    for (const part of entry.parts) {
      if (part.status === 'success') {
        continue;
      }
      if (!entries.value.includes(entry)) {
        return;
      }

      part.status = 'uploading';

      const start = (part.partNumber - 1) * PART_SIZE;
      const end = Math.min(part.partNumber * PART_SIZE, totalSize);
      const blob = entry.file.slice(start, end);

      try {
        const etag = await uploadSinglePart(entry, part, blob, totalSize);
        if (!entries.value.includes(entry)) {
          return;
        }
        part.etag = etag;
        part.status = 'success';
      } catch {
        if (!entries.value.includes(entry) || entry.status === 'error') {
          return;
        }

        part.status = 'error';
        markEntryAsError(entry, 'upload_failed');
        releaseQueueSlot();
        processQueue();
        return;
      }
    }

    if (!entries.value.includes(entry)) {
      return;
    }

    await completeMultipart(entry);
  };

  const uploadSinglePart = (
    entry: FileUploadEntry,
    part: NonNullable<FileUploadEntry['parts']>[number],
    blob: Blob,
    totalSize: number
  ): Promise<string> => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      entry.xhr = xhr;
      resetStallTimer(entry);

      const completedPartsSize = (entry.parts || [])
        .filter((currentPart) => currentPart.status === 'success')
        .reduce((sum, currentPart) => {
          const start = (currentPart.partNumber - 1) * PART_SIZE;
          const end = Math.min(currentPart.partNumber * PART_SIZE, totalSize);
          return sum + (end - start);
        }, 0);

      xhr.upload.onprogress = (event) => {
        if (!entries.value.includes(entry)) {
          return;
        }

        resetStallTimer(entry);
        if (event.lengthComputable) {
          entry.progress = Math.round(
            ((completedPartsSize + event.loaded) / totalSize) * 100
          );
        }
      };

      xhr.onload = () => {
        clearStallTimer(entry);
        entry.xhr = null;

        if (!entries.value.includes(entry)) {
          reject(new Error('ENTRY_REMOVED'));
          return;
        }

        if (xhr.status >= 200 && xhr.status < 300) {
          const etag = xhr.getResponseHeader('ETag');
          if (etag) {
            resolve(etag);
          } else {
            reject(new Error('MISSING_ETAG'));
          }
        } else {
          reject(new Error(`PART_UPLOAD_FAILED_${xhr.status}`));
        }
      };

      xhr.onerror = () => {
        clearStallTimer(entry);
        entry.xhr = null;
        reject(new Error('PART_UPLOAD_NETWORK_ERROR'));
      };

      xhr.onabort = () => {
        clearStallTimer(entry);
        entry.xhr = null;
        reject(new Error('PART_UPLOAD_ABORTED'));
      };

      xhr.open('PUT', part.presignedUrl);
      xhr.send(blob);
    });
  };

  const completeMultipart = async (entry: FileUploadEntry) => {
    if (!entry.uploadId || !entry.s3Key || !entry.parts) {
      if (entries.value.includes(entry)) {
        markEntryAsError(entry, 'finalization_failed');
        releaseQueueSlot();
        processQueue();
      }
      return;
    }

    const partsPayload = entry.parts.map((part) => ({
      partNumber: part.partNumber,
      etag: part.etag as string,
    }));

    for (let attempt = 0; attempt < MAX_RETRY; attempt += 1) {
      try {
        await completeMultipartMutation({
          s3Key: entry.s3Key,
          uploadId: entry.uploadId,
          parts: partsPayload,
        });

        if (!entries.value.includes(entry)) {
          return;
        }

        entry.status = 'success';
        entry.progress = 100;
        entry.error = null;
        entry.errorCode = null;
        releaseQueueSlot();
        processQueue();
        return;
      } catch {
        if (attempt < MAX_RETRY - 1) {
          await new Promise((resolve) => {
            setTimeout(resolve, (2 ** attempt) * 1000);
          });
        }
      }
    }

    if (entries.value.includes(entry)) {
      markEntryAsError(entry, 'finalization_failed');
      releaseQueueSlot();
      processQueue();
    }
  };

  const processQueue = () => {
    if (!userActionId.value) {
      return;
    }

    while (activeCount.value < MAX_CONCURRENT) {
      const nextEntry = entries.value.find((entry) => entry.status === 'pending');
      if (!nextEntry) {
        break;
      }

      activeCount.value += 1;
      nextEntry.status = 'uploading';

      if (nextEntry.file.size > MULTIPART_THRESHOLD) {
        void startMultipartUpload(nextEntry);
      } else {
        void startSingleUpload(nextEntry);
      }
    }
  };

  const waitForEntriesToSettle = async (pendingEntries: FileUploadEntry[]) => {
    while (
      pendingEntries.some((entry) => (
        entries.value.includes(entry)
        && (entry.status === 'pending' || entry.status === 'uploading')
      ))
    ) {
      await new Promise((resolve) => {
        setTimeout(resolve, 25);
      });
    }
  };

  const uploadFiles = async (filesToUpload: FileList | File[]) => {
    uploadError.value = null;
    clearAll();

    const files = Array.from(filesToUpload);
    if (!files.length) {
      return;
    }

    if (!userActionId.value) {
      uploadError.value = 'missing_user_action';
      return;
    }

    const nextEntries: FileUploadEntry[] = [];
    for (const file of files) {
      const entry = createEntry(file, nextEntries);
      nextEntries.push(entry);
    }

    entries.value = nextEntries;
    processQueue();
    await waitForEntriesToSettle(nextEntries);
  };

  const uploadFile = async (file: File) => {
    await uploadFiles([file]);
  };

  const removeFile = (index: number) => {
    const entry = entries.value[index];
    if (!entry) {
      return;
    }

    entries.value.splice(index, 1);

    if (entry.xhr) {
      entry.xhr.abort();
    }
    clearStallTimer(entry);
    if (entry.status === 'uploading') {
      releaseQueueSlot();
    }
  };

  const onFileRemoved = (index: number) => {
    // FieldMultiFileUploader also updates modelValue; the files watcher handles
    // normal cleanup. This fallback keeps direct/legacy callers safe.
    removeFile(index);
  };

  const renameFile = ({ index, filename }: { index: number; filename: string }) => {
    const entry = entries.value[index];
    if (!entry) {
      return;
    }

    const trimmedFilename = filename.trim();
    entry.filename = trimmedFilename || entry.originalFilename;
  };

  const retryFile = (index: number) => {
    const entry = entries.value[index];
    if (
      !entry
      || entry.retryCount >= MAX_RETRY
      || !isRetryableUploadError(entry.errorCode)
    ) {
      return;
    }

    clearStallTimer(entry);
    if (entry.xhr) {
      entry.xhr.abort();
    }

    const validationError = getValidationError(
      entry.file,
      entries.value.filter((currentEntry) => currentEntry !== entry)
    );
    if (validationError) {
      rejectEntryForValidationError(entry, validationError);
      return;
    }

    entry.retryCount += 1;
    entry.status = 'pending';
    entry.progress = 0;
    entry.error = null;
    entry.errorCode = null;
    entry.xhr = null;

    const canResumeMultipartUpload = Boolean(
      entry.uploadId
      && entry.s3Key
      && entry.parts?.some((part) => part.status === 'success')
    );

    if (entry.parts) {
      if (!canResumeMultipartUpload) {
        entry.s3Key = null;
        entry.uploadId = null;
        entry.parts = null;
      } else {
        for (const part of entry.parts) {
          if (part.status !== 'success') {
            part.status = 'pending';
          }
        }
      }
    } else {
      entry.s3Key = null;
    }

    uploadError.value = null;
    processQueue();
  };

  const clearAll = () => {
    const currentEntries = entries.value.slice();
    entries.value = [];
    activeCount.value = 0;

    for (const entry of currentEntries) {
      if (entry.xhr) {
        entry.xhr.abort();
      }
      clearStallTimer(entry);
    }

    uploadError.value = null;
  };

  const clearFiles = () => {
    clearAll();
  };

  watch(
    () => files.value.slice(),
    (newFiles) => {
      const existingFiles = new Set(entries.value.map((entry) => entry.file));
      const addedFiles = newFiles.filter((file) => !existingFiles.has(file));

      const newFilesSet = new Set(newFiles);
      const removedEntries = entries.value.filter(
        (entry) => !newFilesSet.has(entry.file)
      );

      if (removedEntries.length) {
        entries.value = entries.value.filter((entry) => newFilesSet.has(entry.file));

        for (const entry of removedEntries) {
          if (entry.xhr) {
            entry.xhr.abort();
          }
          clearStallTimer(entry);
          if (entry.status === 'uploading') {
            releaseQueueSlot();
          }
        }
      }

      for (const file of addedFiles) {
        entries.value.push(createEntry(file));
      }

      processQueue();
    },
    { immediate: true }
  );

  watch(userActionId, (newId, oldId) => {
    // s3 keys are namespaced by action id, so anything staged for the previous
    // action would be rejected by the API if it rode along with this one.
    if (oldId && newId !== oldId) {
      files.value = [];
      clearAll();
    }

    if (newId > 0) {
      processQueue();
    }
  });

  const onBeforeUnload = (event: BeforeUnloadEvent) => {
    event.preventDefault();
    event.returnValue = '';
  };

  watch(isUploading, (uploading) => {
    if (uploading) {
      window.addEventListener('beforeunload', onBeforeUnload);
      return;
    }

    window.removeEventListener('beforeunload', onBeforeUnload);
  });

  onBeforeUnmount(() => {
    window.removeEventListener('beforeunload', onBeforeUnload);
  });

  return {
    uploadStates,
    completedFiles,
    isUploading,
    hasErrors,
    allUploaded,
    onFileRemoved,
    renameFile,
    retryFile,
    clearAll,

    // Legacy bridge until the UGC screen is migrated in phase 5.
    uploadedFiles,
    uploadError,
    uploadFile,
    uploadFiles,
    removeFile,
    clearFiles,
  };
};

export type { UploadedFile };
